import "@/App.css";
import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import Home from "@/pages/Home";
import Book from "@/pages/Book";
import Services from "@/pages/Services";
import PamperParties from "@/pages/PamperParties";
import Extensions from "@/pages/Extensions";
import HairLoss from "@/pages/HairLoss";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Academy from "@/pages/Academy";
import CourseDetail from "@/pages/CourseDetail";
import Account from "@/pages/Account";
import AuthCallback from "@/pages/AuthCallback";
import MagicLink from "@/pages/MagicLink";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Gallery from "@/pages/Gallery";
import Reviews from "@/pages/Reviews";
import Admin from "@/pages/Admin";
import ColourMatch from "@/pages/ColourMatch";
import ExtensionsQuiz from "@/pages/ExtensionsQuiz";
import TradeAccount from "@/pages/TradeAccount";
import Legal from "@/pages/Legal";
import CookieBanner from "@/components/CookieBanner";
import WelcomePopup from "@/components/WelcomePopup";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import GiftVouchers from "@/pages/GiftVouchers";
import FreeEducation from "@/pages/FreeEducation";
import Bridal from "@/pages/Bridal";
import BridalPay from "@/pages/BridalPay";
import axios from "axios";
import { API } from "@/lib/auth";
import { initAnalytics, trackPageView, pingPageView } from "@/lib/analytics";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    trackPageView(pathname);
    pingPageView(pathname);
  }, [pathname]);
  return null;
};

const AnalyticsBootstrap = () => {
  useEffect(() => {
    axios.get(`${API}/site/content`)
      .then((r) => {
        const a = r.data?.analytics || {};
        if (a.ga4_id || a.meta_pixel_id) initAnalytics(a);
      })
      .catch(() => {});
    // Re-init after cookie consent is granted
    const onConsent = () => {
      axios.get(`${API}/site/content`)
        .then((r) => {
          const a = r.data?.analytics || {};
          if (a.ga4_id || a.meta_pixel_id) initAnalytics(a);
        })
        .catch(() => {});
    };
    window.addEventListener("cookie-consent-granted", onConsent);
    return () => window.removeEventListener("cookie-consent-granted", onConsent);
  }, []);
  return null;
};

// Global click tracker — records first-party click events for any element that
// carries a `data-testid` or `data-track` attribute. Uses event delegation so
// new components are covered automatically with zero wiring.
const ClickTracker = () => {
  useEffect(() => {
    const handler = (e) => {
      const el = e.target?.closest?.("[data-track], [data-testid]");
      if (!el) return;
      const name = el.getAttribute("data-track") || el.getAttribute("data-testid");
      // Skip noisy admin/utility widgets and pure form inputs
      if (!name) return;
      if (name.startsWith("admin-") || name.startsWith("quiz-tab-")) return;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      // Only interactive elements
      if (!(tag === "a" || tag === "button" || el.getAttribute("role") === "button" || el.onclick)) return;
      import("@/lib/analytics").then(({ pingClick }) => pingClick(name));
    };
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true });
  }, []);
  return null;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Navbar />
      <main data-testid="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/book" element={<Book />} />
          <Route path="/services" element={<Services />} />
          <Route path="/pamper-parties" element={<PamperParties />} />
          <Route path="/hair-extensions" element={<Extensions />} />
          <Route path="/hair-loss" element={<HairLoss />} />
          <Route path="/academy" element={<Academy />} />
          <Route path="/academy/:slug" element={<CourseDetail />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/account" element={<Account />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/colour-match" element={<ColourMatch />} />
          <Route path="/quiz" element={<ExtensionsQuiz />} />
          <Route path="/trade" element={<TradeAccount />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/gift-vouchers" element={<GiftVouchers />} />
          <Route path="/free-education" element={<FreeEducation />} />
          <Route path="/bridal" element={<Bridal />} />
          <Route path="/bridal/pay/:token" element={<BridalPay />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route path="/auth/magic" element={<MagicLink />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
      <WelcomePopup />
      <AnalyticsBootstrap />
      <ClickTracker />
    </>
  );
};

function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ScrollToTop />
            <AppRouter />
            <Toaster position="bottom-right" />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
